import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, Tag, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SocialMeta from "@/components/social/SocialMeta";
import ShareBar from "@/components/social/ShareBar";
import SaveButton from "@/components/saved/SaveButton";
import PriorityZipCodesArticle, {
  PRIORITY_ZIP_SLUG,
} from "@/components/insights/PriorityZipCodesArticle";
import BlackBusinessGapArticle, {
  BLACK_BUSINESS_GAP_SLUG,
} from "@/components/insights/BlackBusinessGapArticle";
import UnbankedUnderbankedArticle, {
  UNBANKED_SLUG,
} from "@/components/insights/UnbankedUnderbankedArticle";
import JamesBaldwinArticle, {
  JAMES_BALDWIN_SLUG,
} from "@/components/insights/JamesBaldwinArticle";
import RelatedInsights from "@/components/insights/RelatedInsights";

const ArticleDetail = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: article, isLoading, error } = useQuery({
    queryKey: ["article", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("slug", slug!)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("Article not found");
      return data;
    },
    enabled: !!slug,
  });

  const readTime = article
    ? `${Math.max(1, Math.ceil((article.body?.length || 0) / 1000))} min read`
    : "";

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {article && (
        <SocialMeta
          title={`${article.title} — The Emerging Market`}
          description={article.excerpt}
          path={`/insights/${article.slug}`}
          image={article.image_url}
          type="article"
          publishedTime={article.published_at}
          section={article.category}
          tags={article.tags}
          imageWidth={1920}
          imageHeight={1080}
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt ?? undefined,
            image: article.image_url ?? undefined,
            datePublished: article.published_at ?? undefined,
            dateModified: article.updated_at ?? undefined,
            author: { "@type": "Person", name: article.author },
            publisher: {
              "@type": "Organization",
              name: "The Emerging Market",
              url: "https://emrkt.co",
            },
            articleSection: article.category ?? undefined,
            keywords: article.tags?.join(", ") ?? undefined,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://emrkt.co/insights/${article.slug}`,
            },
          }}
        />
      )}

      <article className="pt-36 pb-20">
        <div className="container mx-auto px-6 max-w-3xl">
          {/* Back link */}
          <Link to="/insights">
            <Button variant="ghost" size="sm" className="mb-8 -ml-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back to Insights
            </Button>
          </Link>

          {isLoading && (
            <div className="space-y-4 animate-pulse">
              <div className="h-6 w-32 bg-muted rounded" />
              <div className="h-10 w-full bg-muted rounded" />
              <div className="h-4 w-48 bg-muted rounded" />
              <div className="h-64 w-full bg-muted rounded mt-8" />
            </div>
          )}

          {error && (
            <div className="text-center py-20">
              <h2 className="font-display text-2xl font-bold mb-3">Article not found</h2>
              <p className="text-muted-foreground mb-6">
                The article you're looking for doesn't exist or is no longer published.
              </p>
              <Link to="/insights">
                <Button>Return to Insights</Button>
              </Link>
            </div>
          )}

          {article && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Category & meta */}
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  {article.category}
                </span>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {readTime}
                </span>
              </div>

              {/* Title */}
              <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-5">
                {article.title}
              </h1>

              {/* Excerpt */}
              {article.excerpt && (
                <p className="text-lg text-muted-foreground leading-relaxed mb-6 border-l-2 border-primary/30 pl-4">
                  {article.excerpt}
                </p>
              )}

              {/* Author & date */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-10 pb-8 border-b border-border">
                <span className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  {article.author}
                </span>
                {article.published_at && (
                  <span className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    {format(new Date(article.published_at), "MMMM d, yyyy")}
                  </span>
                )}
                {article.tags && article.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-[11px] bg-muted px-2 py-0.5 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Hero image */}
              {article.image_url && (
                <img
                  src={article.image_url}
                  alt={article.title}
                  className="w-full rounded-xl mb-10 object-cover max-h-[400px]"
                />
              )}

              {/* Body */}
              {article.slug === PRIORITY_ZIP_SLUG ? (
                <PriorityZipCodesArticle />
              ) : article.slug === BLACK_BUSINESS_GAP_SLUG ? (
                <BlackBusinessGapArticle />
              ) : article.slug === UNBANKED_SLUG ? (
                <UnbankedUnderbankedArticle />
              ) : article.slug === JAMES_BALDWIN_SLUG ? (
                <JamesBaldwinArticle />
              ) : (
                <div className="prose max-w-none prose-headings:font-display prose-headings:font-bold prose-headings:text-foreground prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:leading-relaxed prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-hr:border-border prose-blockquote:border-primary/30 prose-blockquote:text-muted-foreground">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {article.body}
                  </ReactMarkdown>
                </div>
              )}

              {/* Sources */}
              {Array.isArray((article as any).citations) && (article as any).citations.length > 0 && (
                <section className="mt-12 pt-6 border-t border-border">
                  <h2 className="font-display text-lg font-semibold mb-3">Sources</h2>
                  <ol className="list-decimal pl-5 space-y-2 text-sm text-muted-foreground">
                    {((article as any).citations as Array<{
                      label: string; url: string; publisher?: string; accessed_at?: string;
                    }>).map((c, i) => (
                      <li key={i}>
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="text-primary hover:underline"
                        >
                          {c.label || c.url}
                        </a>
                        {c.publisher && <span> — {c.publisher}</span>}
                        {c.accessed_at && <span className="text-xs"> (accessed {c.accessed_at})</span>}
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {/* Share */}
              <div className="mt-12 pt-6 border-t border-border">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <ShareBar
                    path={`/insights/${article.slug}`}
                    title={article.title}
                    text={article.excerpt ?? undefined}
                  />
                  <SaveButton
                    item={{
                      item_type: "article",
                      item_id: article.slug,
                      title: article.title,
                      url: `/insights/${article.slug}`,
                      source: "Insights",
                      cover_image_url: article.image_url,
                      summary: article.excerpt ?? undefined,
                      metadata: { category: article.category, tags: article.tags },
                    }}
                  />
                </div>
              </div>

              {/* Related insights */}
              <RelatedInsights
                currentSlug={article.slug}
                category={article.category}
                tags={article.tags}
              />

              {/* Bottom nav */}
              <div className="mt-16 pt-8 border-t border-border">
                <Link to="/insights">
                  <Button variant="outline" className="gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Back to All Insights
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </article>

      <SiteFooter />
    </div>
  );
};

export default ArticleDetail;
